import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterPanel, type FilterField } from '@/components/common/AdvancedFilter';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useOrgGeoFilterOptions } from '@/hooks/useOrgGeoFilterOptions';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import {
  getCompanyDepartmentFilterFields,
  getDateFromToFilterFields,
  getGeoFilterFields,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { bookEditionsQueryOptions, bookTypesQueryOptions } from '../api/product.api';
import { LOOKUP_PAGE_SIZE } from '../constants/product.constants';
import { getBookFilterFields } from '../utils/productFilterFields';

const BOOK_LOOKUP_PARAMS = { page: 1, pageSize: LOOKUP_PAGE_SIZE, isActive: true } as const;

interface ProductFilterPanelProps {
  filters: UrlSyncedFilters;
  pageFields: FilterField[];
}

export function ProductFilterPanel({ filters, pageFields }: ProductFilterPanelProps) {
  const { filterValues } = filters;

  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const { data: bookTypes } = useQuery(bookTypesQueryOptions(BOOK_LOOKUP_PARAMS));
  const { data: editions } = useQuery(bookEditionsQueryOptions(BOOK_LOOKUP_PARAMS));
  const orgGeoOptions = useOrgGeoFilterOptions(filterValues);

  const fields: FilterField[] = [
    ...getDateFromToFilterFields(),
    getZonalManagerFilterField(zonalManagers),
    ...getGeoFilterFields(filterValues, orgGeoOptions),
    ...pageFields,
    ...getBookFilterFields({ bookTypes: bookTypes?.data, editions: editions?.data }),
    ...getCompanyDepartmentFilterFields(orgGeoOptions),
  ];

  return <AdvancedFilterPanel fields={fields} {...getFilterPanelProps(filters)} />;
}
